import React, { useState, useEffect, useMemo } from 'react';
import { GraduationCap, MapPin, Heart, ArrowRight, ArrowLeft, Check, Loader } from 'lucide-react';
import api, { getCached } from '../config/api';
import { hobbiesByCategory } from '../data/hobbies';
import { universityLocations } from '../data/universityLocations';

const OTHER_SCHOOL_VALUE = '__other__';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' }, { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

const POPULAR_HOBBIES = [
  'Basketball', 'Tennis', 'Running', 'Hiking', 'Music', 'Photography',
  'Gaming', 'Cooking', 'Reading', 'Art', 'Programming', 'Travel',
  'Fitness', 'Dancing', 'Volunteering', 'Movies', 'Sports', 'Swimming'
];

const NewUserSetup = ({ user, sessionId, onComplete }) => {
  const [step, setStep] = useState(0);
  const [locationCity, setLocationCity] = useState('');
  const [locationState, setLocationState] = useState('');
  const [schoolType, setSchoolType] = useState(''); // 'high_school' | 'university' | 'none'
  const [school, setSchool] = useState(''); // selected university from dropdown (or OTHER_SCHOOL_VALUE)
  const [schoolOtherName, setSchoolOtherName] = useState(''); // when "Other" selected, custom name
  const [highSchoolName, setHighSchoolName] = useState('');
  const [schools, setSchools] = useState([]);
  const [hobbies, setHobbies] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [lookingFor, setLookingFor] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [useLocation, setUseLocation] = useState(false);
  const [locationText, setLocationText] = useState('');

  useEffect(() => {
    getCached('/schools', { ttlMs: 5 * 60 * 1000 })
      .then(res => setSchools(res.data || []))
      .catch(() => {});
  }, []);

  // Combined list: API schools + universities from universityLocations (no duplicates)
  const allSchoolsList = useMemo(() => {
    const fromApi = Array.isArray(schools) ? schools : [];
    const fromMap = Object.keys(universityLocations);
    const set = new Set([...fromApi, ...fromMap]);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [schools]);

  // Filter universities by selected city/state (from universityLocations)
  const schoolsInLocation = useMemo(() => {
    if (!locationState) return allSchoolsList;
    const stateUpper = locationState.trim().toUpperCase();
    const cityLower = locationCity.trim().toLowerCase();
    return allSchoolsList.filter(name => {
      const loc = universityLocations[name];
      if (!loc) return false;
      if (loc.state !== stateUpper) return false;
      if (cityLower && loc.city && loc.city.toLowerCase() !== cityLower) return false;
      return true;
    });
  }, [allSchoolsList, locationState, locationCity]);

  useEffect(() => {
    if (user?.location?.city && user?.location?.state) {
      setLocationCity(user.location.city || '');
      setLocationState(user.location.state || '');
      setLocationText(`${user.location.city}, ${user.location.state}`);
    }
  }, [user]);

  const toggleHobby = (hobby) => {
    setHobbies(prev =>
      prev.includes(hobby) ? prev.filter(h => h !== hobby) : [...prev, hobby]
    );
  };

  const handleFinish = async () => {
    setIsSaving(true);
    try {
      const updates = {};
      const schoolValue = schoolType === 'high_school'
        ? highSchoolName.trim()
        : schoolType === 'university'
          ? (school === OTHER_SCHOOL_VALUE ? schoolOtherName.trim() : school)
          : null;
      if (schoolValue) updates.school = schoolValue;
      if (locationCity.trim() || locationState.trim()) {
        updates.location = {
          ...(user?.location || {}),
          city: locationCity.trim() || user?.location?.city || '',
          state: locationState.trim() || user?.location?.state || '',
        };
      }
      if (hobbies.length > 0) updates.hobbies = hobbies;
      if (preferences.length > 0) updates.preferences = preferences;
      if (lookingFor.trim()) updates.lookingFor = lookingFor.trim();

      if (Object.keys(updates).length > 0) {
        const res = await api.put('/auth/profile', updates, {
          headers: { 'x-session-id': sessionId }
        });
        if (res.data) {
          localStorage.setItem('user', JSON.stringify(res.data));
        }
      }
      localStorage.setItem('hasCompletedSetup', 'true');
      onComplete();
    } catch {
      localStorage.setItem('hasCompletedSetup', 'true');
      onComplete();
    }
  };

  const togglePreference = (pref) => {
    setPreferences(prev =>
      prev.includes(pref) ? prev.filter(p => p !== pref) : [...prev, pref]
    );
  };

  const handleSkip = () => {
    localStorage.setItem('hasCompletedSetup', 'true');
    onComplete();
  };

  const steps = [
    {
      title: `Hey ${user?.name?.split(' ')[0] || 'there'}! Let's get you set up`,
      subtitle: 'Just a few quick questions to personalize your experience',
      icon: '👋',
    },
    {
      title: "Where are you located?",
      subtitle: "City and state help us show schools and clubs near you",
      icon: '📍',
    },
    {
      title: 'Are you currently in school or university?',
      subtitle: 'This helps us show you the right clubs and events',
      icon: '🎓',
    },
    {
      title: schoolType === 'high_school' ? "What's your high school name?" : schoolType === 'university' ? 'Which university do you belong to?' : 'All good — you can add this later',
      subtitle: schoolType === 'high_school' ? 'We\'ll find clubs and activities near you' : schoolType === 'university' ? 'Find clubs and events at your school' : 'You can set your school in Profile anytime',
      icon: '🏫',
    },
    {
      title: 'What are you into?',
      subtitle: 'Pick a few interests so we can recommend the right clubs',
      icon: '❤️',
    },
    {
      title: 'A few preferences',
      subtitle: 'What kind of groups do you want to see?',
      icon: '⚙️',
    },
  ];

  const currentStep = steps[step];

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-gray-100 dark:bg-gray-800">
          <div
            className="h-full bg-indigo-500 transition-all duration-500 ease-out"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>

        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <span className="text-4xl mb-4 block">{currentStep.icon}</span>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {currentStep.title}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {currentStep.subtitle}
            </p>
          </div>

          {/* Step content */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-[#222] rounded-2xl">
                <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">School or university</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">High school or university name</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-[#222] rounded-2xl">
                <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center flex-shrink-0">
                  <Heart className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Your interests</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Get personalized recommendations</p>
                </div>
              </div>
              {locationText && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-[#222] rounded-2xl">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Your location</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{locationText}</p>
                  </div>
                </div>
              )}
              <p className="text-xs text-center text-gray-400 dark:text-gray-500 mt-2">Takes less than 30 seconds</p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">State</label>
                <select
                  value={locationState}
                  onChange={(e) => setLocationState(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-xl text-[15px] font-medium border border-gray-200 dark:border-[#333] bg-white dark:bg-[#222] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
                >
                  <option value="">Select state</option>
                  {US_STATES.map(({ code, name }) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">City</label>
                <input
                  type="text"
                  value={locationCity}
                  onChange={(e) => setLocationCity(e.target.value)}
                  placeholder="e.g. Seattle"
                  className="w-full px-4 py-3.5 rounded-xl text-[15px] font-medium border border-gray-200 dark:border-[#333] bg-white dark:bg-[#222] text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">We’ll show schools and universities in your area in the next step.</p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setSchoolType('high_school')}
                className={`w-full flex items-center gap-4 p-4 rounded-xl text-left border-2 transition-all ${
                  schoolType === 'high_school'
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/20'
                    : 'border-gray-200 dark:border-[#333] bg-gray-50 dark:bg-[#222] hover:border-indigo-300'
                }`}
              >
                <span className="text-2xl">🏫</span>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">High school</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">I'm in high school</p>
                </div>
                {schoolType === 'high_school' && <Check className="h-5 w-5 text-indigo-600 dark:text-indigo-400 ml-auto" />}
              </button>
              <button
                type="button"
                onClick={() => setSchoolType('university')}
                className={`w-full flex items-center gap-4 p-4 rounded-xl text-left border-2 transition-all ${
                  schoolType === 'university'
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/20'
                    : 'border-gray-200 dark:border-[#333] bg-gray-50 dark:bg-[#222] hover:border-indigo-300'
                }`}
              >
                <span className="text-2xl">🎓</span>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">University</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">I'm in university or college</p>
                </div>
                {schoolType === 'university' && <Check className="h-5 w-5 text-indigo-600 dark:text-indigo-400 ml-auto" />}
              </button>
              <button
                type="button"
                onClick={() => setSchoolType('none')}
                className={`w-full flex items-center gap-4 p-4 rounded-xl text-left border-2 transition-all ${
                  schoolType === 'none'
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/20'
                    : 'border-gray-200 dark:border-[#333] bg-gray-50 dark:bg-[#222] hover:border-indigo-300'
                }`}
              >
                <span className="text-2xl">👋</span>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Not right now</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">I'm not in school or prefer not to say</p>
                </div>
                {schoolType === 'none' && <Check className="h-5 w-5 text-indigo-600 dark:text-indigo-400 ml-auto" />}
              </button>
            </div>
          )}

          {step === 3 && (
            <div>
              {schoolType === 'high_school' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Enter your high school name (we’ll use your city/state to find clubs near you).</p>
                  <input
                    type="text"
                    value={highSchoolName}
                    onChange={(e) => setHighSchoolName(e.target.value)}
                    placeholder="Enter your high school name"
                    className="w-full px-4 py-3.5 rounded-xl text-[15px] font-medium border border-gray-200 dark:border-[#333] bg-white dark:bg-[#222] text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
                  />
                  {highSchoolName.trim() && (
                    <div className="flex items-center gap-2 px-4 py-3 bg-indigo-50 dark:bg-indigo-500/10 rounded-xl">
                      <Check className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                      <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">{highSchoolName.trim()}</span>
                    </div>
                  )}
                </div>
              )}
              {schoolType === 'university' && (
                <div className="space-y-4">
                  <select
                    value={school}
                    onChange={(e) => {
                      setSchool(e.target.value);
                      if (e.target.value !== OTHER_SCHOOL_VALUE) setSchoolOtherName('');
                    }}
                    className="w-full px-4 py-3.5 rounded-xl text-[15px] font-medium border border-gray-200 dark:border-[#333] bg-white dark:bg-[#222] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
                  >
                    <option value="">Select your university</option>
                    {(locationState ? schoolsInLocation : allSchoolsList).map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                    <option value={OTHER_SCHOOL_VALUE}>Other school/university</option>
                  </select>
                  {school === OTHER_SCHOOL_VALUE && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">School or university name</label>
                      <input
                        type="text"
                        value={schoolOtherName}
                        onChange={(e) => setSchoolOtherName(e.target.value)}
                        placeholder="Enter your school or university name"
                        className="w-full px-4 py-3.5 rounded-xl text-[15px] font-medium border border-gray-200 dark:border-[#333] bg-white dark:bg-[#222] text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
                      />
                    </div>
                  )}
                  {school && school !== OTHER_SCHOOL_VALUE && (
                    <div className="flex items-center gap-2 px-4 py-3 bg-indigo-50 dark:bg-indigo-500/10 rounded-xl">
                      <Check className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                      <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">{school}</span>
                    </div>
                  )}
                  {school === OTHER_SCHOOL_VALUE && schoolOtherName.trim() && (
                    <div className="flex items-center gap-2 px-4 py-3 bg-indigo-50 dark:bg-indigo-500/10 rounded-xl">
                      <Check className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                      <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">{schoolOtherName.trim()}</span>
                    </div>
                  )}
                </div>
              )}
              {schoolType === 'none' && (
                <p className="text-gray-600 dark:text-gray-400 text-center py-4">
                  You can add your school or university later in Profile if you like.
                </p>
              )}
            </div>
          )}

          {step === 4 && (
            <div>
              <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto pr-1">
                {POPULAR_HOBBIES.map(hobby => (
                  <button
                    key={hobby}
                    onClick={() => toggleHobby(hobby)}
                    className={`px-3.5 py-2 rounded-full text-sm font-medium transition-all ${
                      hobbies.includes(hobby)
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-[#222] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#333] hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10'
                    }`}
                  >
                    {hobby}
                  </button>
                ))}
                {Object.entries(hobbiesByCategory).map(([cat, items]) =>
                  items.filter(h => !POPULAR_HOBBIES.includes(h)).map(hobby => (
                    <button
                      key={hobby}
                      onClick={() => toggleHobby(hobby)}
                      className={`px-3.5 py-2 rounded-full text-sm font-medium transition-all ${
                        hobbies.includes(hobby)
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'bg-gray-100 dark:bg-[#222] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#333] hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10'
                      }`}
                    >
                      {hobby}
                    </button>
                  ))
                )}
              </div>
              {hobbies.length > 0 && (
                <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium mt-3">
                  {hobbies.length} selected
                </p>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">What kind of groups do you want to see?</p>
                <div className="space-y-2">
                  {['school', 'public', 'open'].map(pref => (
                    <label key={pref} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-[#333] bg-gray-50 dark:bg-[#222] cursor-pointer hover:border-indigo-300">
                      <input
                        type="checkbox"
                        checked={preferences.includes(pref)}
                        onChange={() => togglePreference(pref)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200 capitalize">{pref} groups</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">What kind of group are you looking for? (optional)</label>
                <textarea
                  value={lookingFor}
                  onChange={(e) => setLookingFor(e.target.value)}
                  placeholder="e.g. study groups, sports, arts, volunteering..."
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl text-[15px] border border-gray-200 dark:border-[#333] bg-white dark:bg-[#222] text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 resize-none"
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mt-8">
            <div className="flex items-center gap-4">
              {step > 0 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              )}
              <button
                onClick={handleSkip}
                className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                Skip for now
              </button>
            </div>
            {step < steps.length - 1 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={(step === 2 && !schoolType) || (step === 3 && schoolType === 'university' && (!school || (school === OTHER_SCHOOL_VALUE && !schoolOtherName.trim())))}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={handleFinish}
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-all disabled:opacity-60"
              >
                {isSaving ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Let's go!
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewUserSetup;
